import Vue from "vue"
import { Service } from './services/mainService'
import { isJwtExpired } from 'jwt-check-expiration';
import { Buffer } from 'buffer';
import { clearMatomoUserContext, setMatomoUserContext, trackMatomoEvent } from "@/utils/matomo";

const permissaoNovoUsuario = 'NUSR'
const permissaoNovoUsuarioCert = 'NUSERCERT'

export const store = Vue.observable({
    //dados pessoais
    cpf: '',
    email: '',
    idUsuario: '',
    token: false,
    nomeUsuario: '',
    nextStep: '',
    primeiroAcesso: '',
    //ug onde o usuário está logado
    codigoUG: '',
    gestorUG: '',

    //controle de tela
    blockScreen: false,
    alert: [],
    showUGSelector: true,
    forceQuit: false,

    //permissoes 
    termoPendente: null,
    permissoes: [],
    userMenu: [],
    isLowerResolution: false,
    //controles da store
    firstRun: true,

    //ambiente
    ambiente: ''
})

const isValidStorageValue = (value) => {
    return value !== null && value !== undefined && value !== '' && value !== 'null' && value !== 'undefined' && value !== 'false'
}

export const clearSession = () => {
    stopRefreshToken();
    store.token = false;
    store.cpf = '';
    store.email = '';
    store.idUsuario = '';
    store.codigoUG = '';
    store.gestorUG = '';
    store.nomeUsuario = '';
    store.nextStep = ''
    store.primeiroAcesso = ''
    store.permissoes = []
    store.termoPendente = null
    localStorage.clear();
    clearMatomoUserContext();
}


/**
 * Inicialização da store, buscando se há dados válidos na localStorage
 * @since 202112
 */
const carregarDaStorage = () => {
    if (store.token === false) { //app recém-inicializado. Buscar dados em localStorage
        const lsToken = localStorage.getItem('token');
        const lsCpf = localStorage.getItem('cpf');
        const lsEmail = localStorage.getItem('email');
        const lsIdUsuario = localStorage.getItem('idUsuario');
        const lsNome = localStorage.getItem('nomeUsuario');
        const lsPrimeiroAcesso = localStorage.getItem('primeiroAcesso');
        const lsPermissoes = localStorage.getItem('permissoes');
        //dados de ug
        const lsCodigoUG = localStorage.getItem('codUG');
        const lsGestorUG = localStorage.getItem('gestorUG');

        if (isValidStorageValue(lsToken)) {
            store.token = lsToken;
            store.cpf = lsCpf;
            store.email = lsEmail;
            store.idUsuario = lsIdUsuario || getUserIdFromToken(lsToken);
            store.nomeUsuario = lsNome;
            store.primeiroAcesso = lsPrimeiroAcesso;

            if (isValidStorageValue(lsCodigoUG)) {
                store.codigoUG = lsCodigoUG;
                store.gestorUG = isValidStorageValue(lsGestorUG) ? lsGestorUG : '';

                if (!store.gestorUG) {
                    localStorage.removeItem('gestorUG');
                }
            } else {
                localStorage.removeItem('codUG');
                localStorage.removeItem('gestorUG');
            }

            if (lsPermissoes) {
                store.permissoes = JSON.parse(lsPermissoes)
            }

            setMatomoUserContext(store.idUsuario, store.codigoUG);
        }
    }
}

/**
 * Obtém e atualiza tokens, para permitir contato com todas as nossas APIs. 
 * Chamado quando a aplicação é inicializada (em base-layout.vue).
 */
export const getToken = () => {
    //Na primeira execução, precisamos checar se não há conteúdo em localStorage para carregar
    if (store.firstRun) {
        store.firstRun = false;
        carregarDaStorage();
        //checar se o token não expirou. Caso tenha expirado, deslogar o usr
        if (store.token && isJwtExpired(store.token)) {
            store.forceQuit = true;
            return;
        }
    }
}

let refreshTimeoutId = null;

export const refreshToken = async () => {
    stopRefreshToken();
    await Service.$http.post(process.env.VUE_APP_API_USUARIOS + 'auth/refreshToken').then((response) => {
        updateToken(
            response.data.token,
            response.data.cpf,
            response.data.codigoUG,
            response.data.nomeUsuario,
            response.data.primeiroAcesso,
            response.data.permissoes,
            response.data.nomeGestor ?? store.gestorUG,
            response.data.email ?? store.email,
            store.nextStep,
            response.data.idUsuario ?? response.data.ID_USUARIO ?? response.data.IdUsuario
        );

        if (response.data.termosPendentes && response.data.termosPendentes.length > 0) {
            store.termoPendente = response.data.termosPendentes[0];
        }

        if (response.data.permissoes && response.data.permissoes.length > 0)
            store.permissoes = response.data.permissoes

        refreshTimeoutId = setTimeout(() => {
            refreshToken()
        }, 15 * 60 * 1000)
    })
        .catch((error) => {
            console.error(error)
            store.forceQuit = true;
        })
        .finally(() => {
            store.blockScreen = false;
        })
}

/**
 * Cancela a cadeia de renovação periódica do token.
 * Deve ser chamado sempre que a sessão é encerrada (logout manual, inatividade,
 * logout disparado por outra aba, token expirado), para não deixar um refresh
 * órfão rodando após a sessão já ter sido limpa.
 */
export const stopRefreshToken = () => {
    if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
        refreshTimeoutId = null;
    }
}

const INATIVIDADE_LIMITE_MS = 60 * 60 * 1000; // 60 minutos
const EVENTOS_ATIVIDADE = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
let inatividadeTimeoutId = null;

const reiniciarTimerInatividade = () => {
    if (inatividadeTimeoutId) {
        clearTimeout(inatividadeTimeoutId);
    }
    inatividadeTimeoutId = setTimeout(() => {
        store.forceQuit = true;
    }, INATIVIDADE_LIMITE_MS);
}

/**
 * Inicia o monitoramento de atividade do usuário, deslogando-o após
 * INATIVIDADE_LIMITE_MS sem interação (mouse, teclado, scroll ou toque).
 */
export const startInactivityWatcher = () => {
    EVENTOS_ATIVIDADE.forEach((evento) => window.addEventListener(evento, reiniciarTimerInatividade, { capture: true }));
    reiniciarTimerInatividade();
}

export const stopInactivityWatcher = () => {
    EVENTOS_ATIVIDADE.forEach((evento) => window.removeEventListener(evento, reiniciarTimerInatividade, { capture: true }));
    if (inatividadeTimeoutId) {
        clearTimeout(inatividadeTimeoutId);
        inatividadeTimeoutId = null;
    }
}

/**
 * Atualiza dados do usuário (token, cpf, UG e nome).
 * @param string newToken token atualizado
 * @param string cpf 
 * @param string codUG 6 dígitos
 * @param string nome do usuário
 * @param string primeiroAcesso indica se é o primeiro acesso do usuario (S ou N)
 * 
 * @since 20211210
 */

export const updateToken = async (newToken, newCpf, codUG, nomeUsuario, primeiroAcesso, permissoes, nomeGestor = undefined, email = null, nextStep = '', idUsuario = null) => {
    if (newToken === false) {
        clearSession();
    } else {
        const codigoUGAnterior = store.codigoUG;
        const gestorUGAnterior = store.gestorUG;
        const hasCodigoUG = isValidStorageValue(codUG);
        const hasNomeGestor = isValidStorageValue(nomeGestor);

        store.token = newToken;
        store.cpf = newCpf;

        const resolvedIdUsuario = idUsuario ?? getUserIdFromToken(newToken);
        store.idUsuario = resolvedIdUsuario ? String(resolvedIdUsuario) : '';

        if (email) {
            store.email = email;
        }

        store.codigoUG = hasCodigoUG ? codUG : '';
        if (hasCodigoUG) {
            store.gestorUG = nomeGestor === undefined && codigoUGAnterior === codUG
                ? gestorUGAnterior
                : (hasNomeGestor ? nomeGestor : '');
        } else {
            store.gestorUG = '';
        }

        store.nomeUsuario = nomeUsuario;
        store.nextStep = nextStep;
        store.primeiroAcesso = primeiroAcesso;
        store.permissoes = permissoes && permissoes.length > 0 ? permissoes : [];

        localStorage.setItem('token', newToken);
        localStorage.setItem('cpf', newCpf);

        if (store.idUsuario) {
            localStorage.setItem('idUsuario', store.idUsuario);
        } else {
            localStorage.removeItem('idUsuario');
        }

        if (email) {
            localStorage.setItem('email', email);
        }

        if (hasCodigoUG) {
            localStorage.setItem('codUG', codUG);
            if (store.gestorUG) {
                localStorage.setItem('gestorUG', store.gestorUG);
            } else {
                localStorage.removeItem('gestorUG');
            }
        } else {
            localStorage.removeItem('codUG');
            localStorage.removeItem('gestorUG');
        }

        localStorage.setItem('nomeUsuario', nomeUsuario);
        localStorage.setItem('primeiroAcesso', primeiroAcesso);
        localStorage.setItem('permissoes', JSON.stringify(store.permissoes));

        setMatomoUserContext(store.idUsuario, store.codigoUG);
    }
}

const trackUgChange = (action, previousCodigoUg, nextCodigoUg) => {
    if (!nextCodigoUg) return;

    trackMatomoEvent({
        category: "UG",
        action,
        name: `${previousCodigoUg || "sem_ug"} -> ${nextCodigoUg}`,
    });
}

export const trackUgChangeRequested = (nextCodigoUg, previousCodigoUg = null) => {
    trackUgChange("troca_solicitada", previousCodigoUg, nextCodigoUg);
}

export const trackUgChangeConfirmed = (nextCodigoUg, previousCodigoUg = null) => {
    trackUgChange("troca_confirmada", previousCodigoUg, nextCodigoUg);
}

export const trackUgChangeFailed = (nextCodigoUg, previousCodigoUg = null) => {
    trackUgChange("troca_falhou", previousCodigoUg, nextCodigoUg);
}

/** Extrai a lista de perfis (roles) de um token, retorna array<string> */
function decodeBase64Url(str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    const padded = str.padEnd(str.length + (4 - (str.length % 4)) % 4, '=');
    return Buffer.from(padded, 'base64').toString('utf8');
}

function decodeJwt(token) {
    const [header, payload] = token.split('.');
    return {
        header: JSON.parse(decodeBase64Url(header)),
        payload: JSON.parse(decodeBase64Url(payload)),
    };
}

function getUserIdFromToken(token) {
    try {
        const decoded = decodeJwt(token);
        const idUsuario = decoded?.payload?.idUsuario
            ?? decoded?.payload?.ID_USUARIO
            ?? decoded?.payload?.IdUsuario
            ?? decoded?.payload?.id_usuario;
        if (typeof idUsuario === 'string') return idUsuario;
        if (typeof idUsuario === 'number' && Number.isFinite(idUsuario)) return String(idUsuario);
    } catch {
        return '';
    }
    return '';
}

const getRolesFromToken = () => {
    if (store?.token) {
        const decoded = decodeJwt(store.token);
        return decoded.payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || [];
    }
    return []
}

//validações de permissão
export const isAdmin = () => {
    const roles = getRolesFromToken()
    return roles.includes('ADM')
}
export const isNewUser = () => {
    const roles = getRolesFromToken()
    return roles.includes(permissaoNovoUsuario) || roles.includes(permissaoNovoUsuarioCert)
}
export const isNewCertUser = () => {
    const roles = getRolesFromToken()
    return roles.includes(permissaoNovoUsuarioCert)
}

//informações do ambiente

export const setarAmbiente = () => {
    const host = window.location.hostname;
    const pathsProducao = ["portal.tcese.tc.br", "portal.tce.se.gov.br"];
    const pathsHomologacao = [
        "portal-hml.tcese.tc.br",
        "portal-hml.tce.se.gov.br",
    ];

    if (pathsProducao.includes(host)) {
        store.ambiente = "prd";
        return
    }
    if (pathsHomologacao.includes(host)) {
        store.ambiente = "hml";
        return
    }
    store.ambiente = "dev";
}

export const isDev = () => store.ambiente === 'dev'

export const isHml = () => store.ambiente === 'hml'

export const isProd = () => store.ambiente === 'prd'
